import { useEffect, useCallback, useRef, useContext, useState } from 'react'
import config from 'constants/config'
import { connect } from 'react-redux'
import { replace } from 'lib/routing/appRouting'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { jwtDecode } from 'jwt-decode'
import { GSILoadComplete, loginGoogle } from 'actions/AuthActions'
import { consumePendingGoogleCollectPhone } from 'lib/auth/googlePhoneCollection'
import { cancelGoogleOneTap, isGoogleButtonAccountModalOpen, isGoogleOneTapSuppressed } from 'lib/auth/googleOneTap'
import { fireInteractionEvent } from 'api/googleTagManager'
import noop from 'lib/function/noop'
import useWhiteLabelAppConfig from 'hooks/useWhiteLabelAppConfig'
import MasterModalContext from 'contexts/MasterModalContext'
import useScreenSize from 'hooks/useScreenSize'
import type { GoogleInitializeData } from 'typings/googleIdentityServices'

const delayValue = 20000

interface Props {
  isLoggedIn: boolean
  onPromptOpen?: () => void
  onPromptClose?: () => void
  disableTapLogin: boolean
}

interface GoogleToken {
  email: string
  family_name: string
  given_name: string
  sub: string
}

function GoogleAuth(props: Props): null {
  const {
    isLoggedIn,
    onPromptClose = noop,
    disableTapLogin,
  } = props

  const { open: anyModalOpen, ids: openModalIds } = useContext(MasterModalContext)
  const gsiLoaded = useAppSelector(state => state.auth.gsiLoaded)
  const accountAccessGoogleButtonOpen = isGoogleButtonAccountModalOpen(openModalIds)

  // Use these callbacks via refs so they won't trigger the `useEffect` to re-run if they change
  const onCloseRef = useRef(onPromptClose)
  onCloseRef.current = onPromptClose

  const timeoutState = useRef<number | undefined>(undefined)
  const initializedLoginUri = useRef<string | undefined>(undefined)
  const [hasDismissed, setHasDismissed] = useState<boolean>(false)

  const dispatch = useAppDispatch()
  const mountTime = useRef<number>(new Date().getTime())
  const isMobileSize = useScreenSize() === 'mobile'

  const whiteLabelAppConfig = useWhiteLabelAppConfig()

  const onGoogleSignedIn = useCallback((response: any) => {
    if (!response?.credential) {
      window.google.accounts.id.disableAutoSelect()
      throw new Error('Google Login error')
    }

    const googleData: GoogleToken = jwtDecode(response.credential)

    const data = {
      id_google: googleData.sub,
      email: googleData.email,
      token_id: response.credential,
      givenName: googleData.given_name,
      surname: googleData.family_name,
    }

    fireInteractionEvent({
      category: response.select_by === 'auto' ? 'google-one-tap-login' : 'google-login',
      action: 'clicked',
      label: (new Date().getTime() - mountTime.current).toString(),
    })

    dispatch(loginGoogle(data, { collectPhone: consumePendingGoogleCollectPhone() }))
  }, [dispatch])

  const isLoggedInRef = useRef(isLoggedIn)
  isLoggedInRef.current = isLoggedIn

  useEffect(() => {
    const onGoogleLoad = (whiteLabelAppGoogleLoginURI = '') => {
      dispatch(GSILoadComplete())

      // For review/canary apps using shared auth domain, don't initialize Google here.
      // Google OAuth will happen on the shared auth domain instead.
      // We only need to handle the credential when redirected back.
      if (config.GOOGLE_AUTH_REDIRECT_DOMAIN && !whiteLabelAppGoogleLoginURI) {
        return
      }

      // GIS initialize() must run once per login URI. Re-running it when a modal
      // opens resets the client and aborts an in-flight FedCM get() from the Google button.
      if (initializedLoginUri.current === whiteLabelAppGoogleLoginURI) {
        return
      }
      initializedLoginUri.current = whiteLabelAppGoogleLoginURI

      const params: GoogleInitializeData = {
        use_fedcm: true,
        client_id: config.GOOGLE_APP_ID,
        auto_select: true,
        ...(whiteLabelAppGoogleLoginURI ? { ux_mode: 'redirect', login_uri: whiteLabelAppGoogleLoginURI } : { callback: onGoogleSignedIn }),
      }
      window.google.accounts.id.initialize(params)
    }

    // Handle credential from URL for redirect flows (white-label apps and review/canary apps)
    if (whiteLabelAppConfig.googleLoginURI || config.GOOGLE_AUTH_REDIRECT_DOMAIN) {
      // Read from window.location directly — React Router's location may lag behind history.replaceState
      const windowParams = new URLSearchParams(window.location.search)
      const credential = windowParams.get('credential')
      if (credential) {
        // Remove credential from URL FIRST to prevent JWT leaking via Referer, browser history, or bookmarks.
        // This must happen before onGoogleSignedIn in case the login dispatch throws.
        //
        // Use router replace() instead of native window.history.replaceState.
        // Native replaceState only updates the browser URL bar — it does NOT update the
        // history library's internal location or dispatch LOCATION_CHANGE to router state.
        // This creates a desync: state.router.location.search still contains ?credential=JWT,
        // and any code that reads from the router state to construct URLs (e.g. NuqsAdapter,
        // replaceSearchString, GeoRedirectModal) would inadvertently restore the credential.
        // router replace() atomically updates the browser URL, history.location,
        // AND state.router.location via a synchronous dispatch through appRouterMiddleware.
        windowParams.delete('credential')
        const cleanSearch = windowParams.toString()
        dispatch(replace(window.location.pathname + (cleanSearch ? `?${cleanSearch}` : '')))
        onGoogleSignedIn({ credential })
        return // Don't initialize Google if we already have a credential
      }
    }

    if (window.google?.accounts) {
      // must already be loaded by the time we've mounted, don't need to hook into load function
      onGoogleLoad(whiteLabelAppConfig.googleLoginURI)
    } else {
      // not loaded yet, hook into the callback
      window.onGoogleLibraryLoad = onGoogleLoad
    }
  }, [dispatch, onGoogleSignedIn, whiteLabelAppConfig.googleLoginURI])

  useEffect(() => {
    if (!gsiLoaded || !window.google?.accounts?.id) return

    if (disableTapLogin || isGoogleOneTapSuppressed()) {
      if (timeoutState.current) clearTimeout(timeoutState.current)
      if (disableTapLogin) {
        cancelGoogleOneTap()
      }
      return
    }

    if (anyModalOpen && !accountAccessGoogleButtonOpen) {
      if (timeoutState.current) clearTimeout(timeoutState.current)
      cancelGoogleOneTap()
      return
    }

    if (anyModalOpen) {
      // Account access already cancelled One Tap on mount. cancel() here would
      // abort a fast click on the modal's Google button (FedCM is single-flight).
      if (timeoutState.current) clearTimeout(timeoutState.current)
      return
    }

    if (!config.GOOGLE_ONE_TAP_LOGIN || hasDismissed) return

    // Make sure OneTap does not pop up at same time as LE log in on mobile
    timeoutState.current = window.setTimeout(() => {
      if (!isLoggedInRef.current) {
        window.google.accounts.id.prompt((notification) => {
          if (notification.isDismissedMoment() || notification.isSkippedMoment()) {
            onCloseRef.current()
          }
          if (notification.isSkippedMoment()) setHasDismissed(true)
        })
      }
    // delay for mobile initially and for both if it's to be shown again
    }, isMobileSize || timeoutState.current ? delayValue : 0)

    return () => clearTimeout(timeoutState.current)
  }, [accountAccessGoogleButtonOpen, anyModalOpen, disableTapLogin, gsiLoaded, hasDismissed, isMobileSize])

  return null
}

const mapState = (state: App.State) => ({
  disableTapLogin: state.utm.medium === 'cpc',
})

export default connect(mapState)(GoogleAuth)
